import React, { useState } from "react";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  PrimaryBlueButton,
  SecondaryBlueButton,
  SurveyQuestion
} from "@velaro/velaro-component-lib";
import useSurveys from "@/hooks/useSurveys";
import useTeams from "@/hooks/useTeams";
import useRules from "@/hooks/useRules";
import Select from "@/components/Select";
import { Rule } from "./types";

type Scenario = "survey" | "time-of-day" | "url" | "language" | null;

interface Props {
  close(): void;
  openManualEditor(preset: Partial<Rule>): void;
}

// ─── Scenario cards ───────────────────────────────────────────────────────────
const SCENARIOS: {
  id: Scenario;
  icon: string;
  title: string;
  description: string;
}[] = [
  {
    id: "survey",
    icon: "📋",
    title: "Route by survey answer",
    description:
      'Visitor picks "Sales" → goes to Sales team. One setup creates all the routing rules automatically.'
  },
  {
    id: "language",
    icon: "🌐",
    title: "Route by visitor language",
    description:
      "Spanish speakers → Spanish team. French speakers → French team. Route by browser language automatically."
  },
  {
    id: "time-of-day",
    icon: "🕐",
    title: "Route by time of day",
    description: "Business hours → one team. After hours → another team."
  },
  {
    id: "url",
    icon: "🔗",
    title: "Route by page URL",
    description:
      "Visitors on /pricing go to Sales. Visitors on /support go to the Support team."
  }
];

export default function QuickRuleSetupModal({ close, openManualEditor }: Props) {
  const [scenario, setScenario] = useState<Scenario>(null);
  const [step, setStep] = useState(0);

  // Survey routing state
  const [selectedSurveyId, setSelectedSurveyId] = useState<number | null>(null);
  const [selectedField, setSelectedField] = useState<SurveyQuestion | null>(null);
  const [optionTeamMap, setOptionTeamMap] = useState<Record<string, number>>({});
  const [saving, setSaving] = useState(false);

  const { surveys } = useSurveys();
  const { teams } = useTeams();
  const { add, refresh } = useRules();

  const savedSurveys = surveys.filter((s) => !!s.id);

  const selectedSurvey = savedSurveys.find((s) => s.id === selectedSurveyId);
  const choiceFields = (selectedSurvey?.questions ?? []).filter(
    (q) => (q.type === "choice" || q.type === "checkbox") && q.variable
  );
  const choiceOptions = (selectedField?.options ?? []).filter((o) => o.trim());

  const teamOptions = Array.isArray(teams)
    ? teams.reduce(
        (acc, t) => {
          acc[t.id.toString()] = t.name;
          return acc;
        },
        {} as Record<string, string>
      )
    : {};

  // Count how many options have a team assigned
  const mappedCount = Object.values(optionTeamMap).filter((id) => id > 0).length;
  const canSaveSurveyRouting =
    selectedField && choiceOptions.length > 0 && mappedCount > 0;

  async function saveSurveyRouting() {
    if (!selectedField) return;
    setSaving(true);
    const entries = Object.entries(optionTeamMap).filter(([, teamId]) => teamId > 0);
    let priority = 1;
    for (const [option, teamId] of entries) {
      await add({
        trigger: "route-to-team",
        priority: priority++,
        value: { teamId },
        expression: [
          [
            {
              type: "attribute",
              data: JSON.stringify({
                attribute: selectedField.variable,
                comparator: "equals",
                value: option
              })
            }
          ]
        ]
      } as Rule);
    }
    setSaving(false);
    refresh();   // re-fetch the full list so priorities are correct
    close();
  }

  function handleScenarioSelect(id: Scenario) {
    if (id === "time-of-day") {
      openManualEditor({
        trigger: "route-to-team",
        priority: 1,
        value: { teamId: 0 },
        expression: [
          [{ type: "timeOfDay", data: JSON.stringify({ from: "09:00", to: "17:00" }) }]
        ]
      });
      return;
    }
    if (id === "url") {
      openManualEditor({
        trigger: "route-to-team",
        priority: 1,
        value: { teamId: 0 },
        expression: [
          [{ type: "url", data: JSON.stringify({ comparator: "contains", url: "" }) }]
        ]
      });
      return;
    }
    if (id === "language") {
      openManualEditor({
        trigger: "route-to-team",
        priority: 1,
        value: { teamId: 0 },
        expression: [
          [{ type: "language", data: JSON.stringify({ comparator: "equals", language: "" }) }]
        ]
      });
      return;
    }
    setScenario(id);
  }

  // ─── Scenario picker ─────────────────────────────────────────────────────────
  function renderPicker() {
    return (
      <>
        <ModalHeader title="Quick Rule Setup" onClose={close} />
        <ModalBody>
          <p className="text-sm text-slate-500 mb-4">
            Pick a common scenario and we'll guide you through it — or open the
            full editor for complete control.
          </p>
          <div className="flex flex-col gap-3">
            {SCENARIOS.map((s) => (
              <button
                key={s.id}
                type="button"
                className="flex items-start gap-3 rounded-lg border border-slate-200 p-4 text-left hover:border-blue-400 hover:bg-blue-50 transition-colors"
                onClick={() => handleScenarioSelect(s.id)}
              >
                <span className="text-2xl leading-none">{s.icon}</span>
                <div>
                  <p className="font-medium text-slate-800 text-sm">{s.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{s.description}</p>
                </div>
              </button>
            ))}
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="flex justify-between px-4 py-3 w-full">
            <SecondaryBlueButton label="Cancel" onClick={close} />
            <button
              type="button"
              className="text-sm text-slate-500 hover:text-blue-600 hover:underline"
              onClick={() => {
                openManualEditor({
                  trigger: "route-to-team",
                  priority: 1,
                  value: { teamId: 0 },
                  expression: []
                });
              }}
            >
              Build manually →
            </button>
          </div>
        </ModalFooter>
      </>
    );
  }

  // ─── Survey routing — Step 1: pick field ────────────────────────────────────
  function renderSurveyStep0() {
    const surveySelectOptions = savedSurveys.reduce(
      (acc, s) => {
        acc[s.id!.toString()] = s.name;
        return acc;
      },
      {} as Record<string, string>
    );

    const fieldSelectOptions = choiceFields.reduce(
      (acc, q) => {
        acc[q.variable!] = q.text || q.variable!;
        return acc;
      },
      {} as Record<string, string>
    );

    return (
      <>
        <ModalHeader title="Route by survey answer — Step 1 of 2" onClose={close} />
        <ModalBody>
          <p className="text-sm text-slate-500 mb-4">
            Choose which survey field to route by. Only choice and checkbox fields
            are shown because they have known options.
          </p>

          {savedSurveys.length === 0 ? (
            <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-700">
              You don't have any saved surveys yet. Create one in{" "}
              <a href="/surveys" target="_blank" rel="noreferrer" className="underline font-medium">
                Surveys
              </a>{" "}
              first (opens in a new tab), then come back here.
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Survey
                </label>
                <Select
                  options={surveySelectOptions}
                  value={selectedSurveyId?.toString() ?? ""}
                  onChange={(v) => {
                    setSelectedSurveyId(Number(v));
                    setSelectedField(null);
                    setOptionTeamMap({});
                  }}
                />
              </div>

              {selectedSurveyId && choiceFields.length === 0 && (
                <div className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                  This survey has no choice or checkbox fields. Add one in{" "}
                  <a href="/surveys" target="_blank" rel="noreferrer" className="underline">
                    Surveys
                  </a>{" "}
                  (new tab), then come back.
                </div>
              )}

              {choiceFields.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Field to route by
                  </label>
                  <Select
                    options={fieldSelectOptions}
                    value={selectedField?.variable ?? ""}
                    onChange={(v) => {
                      const q = choiceFields.find((f) => f.variable === v) ?? null;
                      setSelectedField(q);
                      setOptionTeamMap({});
                    }}
                  />
                  {selectedField && (
                    <p className="mt-1 text-xs text-slate-400">
                      Variable:{" "}
                      <code className="font-mono text-blue-500">
                        {"<<"}
                        {selectedField.variable}
                        {">>"}
                      </code>
                    </p>
                  )}
                </div>
              )}

              {selectedField && choiceOptions.length > 0 && (
                <div className="rounded bg-slate-50 border border-slate-200 px-3 py-2">
                  <p className="text-xs text-slate-500 mb-1">Options in this field:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {choiceOptions.map((o) => (
                      <span
                        key={o}
                        className="text-xs bg-white border border-slate-300 rounded px-2 py-0.5 text-slate-700"
                      >
                        {o}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <div className="flex justify-between px-4 py-3 w-full">
            <SecondaryBlueButton label="Back" onClick={() => setScenario(null)} />
            <PrimaryBlueButton
              label="Next →"
              disabled={!selectedField || choiceOptions.length === 0}
              onClick={() => setStep(1)}
            />
          </div>
        </ModalFooter>
      </>
    );
  }

  // ─── Survey routing — Step 2: map options to teams ──────────────────────────
  function renderSurveyStep1() {
    return (
      <>
        <ModalHeader title="Route by survey answer — Step 2 of 2" onClose={close} />
        <ModalBody>
          <p className="text-sm text-slate-500 mb-1">
            Choose which team each answer should go to. Skip options you don't
            want to route.
          </p>
          <p className="text-xs text-slate-400 mb-4">
            Field:{" "}
            <code className="font-mono text-blue-500">
              {"<<"}
              {selectedField?.variable}
              {">>"}
            </code>
          </p>

          <div className="flex flex-col gap-2">
            {choiceOptions.map((option) => (
              <div
                key={option}
                className="flex items-center gap-3 rounded border border-slate-200 bg-white px-3 py-2.5"
              >
                <span className="text-sm font-medium text-slate-700 w-1/3 truncate">
                  {option}
                </span>
                <span className="text-slate-400 text-xs">→</span>
                <div className="flex-1">
                  <Select
                    options={{ "": "— skip —", ...teamOptions }}
                    value={(optionTeamMap[option] ?? 0).toString() === "0"
                      ? ""
                      : (optionTeamMap[option] ?? "").toString()}
                    onChange={(v) =>
                      setOptionTeamMap((prev) => ({
                        ...prev,
                        [option]: Number(v)
                      }))
                    }
                  />
                </div>
              </div>
            ))}
          </div>

          {mappedCount > 0 && (
            <p className="mt-3 text-xs text-slate-500">
              This will create <span className="font-semibold">{mappedCount} rule{mappedCount !== 1 ? "s" : ""}</span>
              {" "}— one per mapped option.
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <div className="flex justify-between px-4 py-3 w-full">
            <SecondaryBlueButton label="← Back" onClick={() => setStep(0)} />
            <PrimaryBlueButton
              label={saving ? "Saving..." : `Save ${mappedCount} Rule${mappedCount !== 1 ? "s" : ""}`}
              disabled={!canSaveSurveyRouting || saving}
              onClick={saveSurveyRouting}
            />
          </div>
        </ModalFooter>
      </>
    );
  }

  // ─── Render ──────────────────────────────────────────────────────────────────
  if (!scenario) return <Modal maxWidth="max-w-lg" show>{renderPicker()}</Modal>;

  if (scenario === "survey") {
    return (
      <Modal maxWidth="max-w-lg" show>
        {step === 0 ? renderSurveyStep0() : renderSurveyStep1()}
      </Modal>
    );
  }

  return null;
}
